import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useScoutsQuery } from '@/features/scouts/api'
import type { Scout } from '@/features/scouts/types'

// Constants
const SCOUTS_LIMIT = 50
const WORK_HOURS = { MIN: 6, MAX: 20 }
const TIME_INTERVAL_MINUTES = 15

type AppointmentType = 'isfp' | 'marketplace'

interface RescheduleDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (scout: Scout, dateTime: string, type: AppointmentType) => void
  customerName?: string
  submitting?: boolean
}

export const RescheduleDialog = ({
  open,
  onClose,
  onConfirm,
  customerName,
  submitting = false,
}: RescheduleDialogProps) => {
  const [selectedScout, setSelectedScout] = useState<Scout | null>(null)
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null)
  const [selectedType, setSelectedType] = useState<AppointmentType>('isfp')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [datePickerError, setDatePickerError] = useState<string | null>(null)

  // API: Fetch scouts with search query when dialog is open
  const {
    data: scoutsData,
    isLoading: scoutsLoading,
    error: scoutsError,
  } = useScoutsQuery(
    {
      limit: SCOUTS_LIMIT,
      q: searchQuery || undefined,
    },
    { enabled: open }
  )

  // Data: Memoized scouts list
  const scouts = useMemo((): Scout[] => (scoutsData?.data as Scout[]) || [], [scoutsData])

  // Date validation: Disable past dates
  const shouldDisableDate = useCallback((date: Date | null) => {
    if (!date) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }, [])

  // Time validation: Restrict hours and minutes
  const shouldDisableTime = useCallback((value: Date, view: 'hours' | 'minutes' | 'seconds') => {
    if (view === 'hours') {
      const hour = value.getHours()
      return hour < WORK_HOURS.MIN || hour > WORK_HOURS.MAX
    }
    if (view === 'minutes') {
      const minutes = value.getMinutes()
      return minutes % TIME_INTERVAL_MINUTES !== 0
    }
    if (view === 'seconds') {
      return value.getSeconds() !== 0
    }
    return false
  }, [])

  // Effect: Default type to iSFP whenever the dialog (re)opens
  useEffect(() => {
    if (open) {
      setSelectedType('isfp')
    }
  }, [open])

  // Handlers
  const handleSubmit = () => {
    if (selectedScout && selectedDateTime) {
      onConfirm(selectedScout, selectedDateTime.toISOString(), selectedType)
    }
  }

  const handleClose = () => {
    // Reset all form state
    setSelectedScout(null)
    setSelectedDateTime(null)
    setSearchQuery('')
    setDatePickerError(null)
    onClose()
  }

  // Form state
  const isSubmitDisabled = !selectedScout || !selectedDateTime || !!datePickerError || submitting

  // Error messages
  const getDatePickerHelperText = () => {
    switch (datePickerError) {
      case 'shouldDisableDate':
        return 'Bitte wählen Sie ein zukünftiges Datum'
      case 'invalidDate':
        return 'Ungültiges Datum'
      case 'shouldDisableTime-hours':
        return `Bitte wählen Sie eine Stunde zwischen ${WORK_HOURS.MIN}:00 und ${WORK_HOURS.MAX}:00`
      case 'shouldDisableTime-minutes':
        return `Bitte wählen Sie Minuten in ${TIME_INTERVAL_MINUTES}-Minuten-Intervallen (z.B. 00, 15, 30, 45)`
      default:
        return datePickerError
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reschedule Appointment</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Customer info */}
          {customerName && (
            <Typography variant="body2" color="text.secondary">
              Creating a new appointment for: <strong>{customerName}</strong>
            </Typography>
          )}

          {/* Scout selection */}
          <Autocomplete
            options={scouts}
            value={selectedScout}
            onChange={(_, newValue) => setSelectedScout(newValue)}
            onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
            getOptionLabel={option => `${option.firstName} ${option.lastName}`}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
            loading={scoutsLoading}
            loadingText="Loading scouts..."
            noOptionsText={scoutsError ? 'Error loading scouts' : 'No scouts found'}
            renderInput={params => (
              <TextField
                {...params}
                label="Search and Select Scout"
                placeholder="Type scout name..."
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {scoutsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.uuid}>
                <div>
                  <div>
                    {option.firstName} {option.lastName}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#666' }}>{option.email}</div>
                </div>
              </li>
            )}
            fullWidth
          />

          {/* Date and time selection */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Select Date & Time"
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              onError={setDatePickerError}
              shouldDisableDate={shouldDisableDate}
              shouldDisableTime={shouldDisableTime}
              ampm={false}
              enableAccessibleFieldDOMStructure={false}
              slots={{
                textField: TextField,
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: getDatePickerHelperText(),
                },
                popper: {
                  sx: {
                    '& .MuiClock-clock': {
                      '& .MuiClockNumber-root.Mui-disabled': {
                        display: 'none',
                      },
                    },
                    '& .MuiMultiSectionDigitalClock-root': {
                      '& .MuiMenuItem-root.Mui-disabled': {
                        display: 'none',
                      },
                    },
                    '& .MuiDigitalClock-root': {
                      '& .MuiMenuItem-root.Mui-disabled': {
                        display: 'none',
                      },
                    },
                    '& .MuiDateCalendar-root': {
                      '& .MuiPickersDay-root.Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.2)',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>

          {/* Appointment Type */}
          <FormControl fullWidth>
            <InputLabel id="reschedule-type-select-label">Type</InputLabel>
            <Select
              labelId="reschedule-type-select-label"
              value={selectedType}
              label="Type"
              onChange={event => setSelectedType(event.target.value as AppointmentType)}
            >
              <MenuItem value="isfp">iSFP</MenuItem>
              <MenuItem value="marketplace">Marketplace</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      {/* Dialog actions */}
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Reschedule
        </Button>
      </DialogActions>
    </Dialog>
  )
}
